import { RAWSTER_SUPPORT_EMAIL } from "./RawsterLayout";
import { BrandedHeading, RawsterBrand } from "./RawsterBrand";

export function RawsterPrivacyPolicy() {
  return (
    <main className="rawster-main rawster-doc">
      <header className="rawster-doc-header">
        <BrandedHeading as="h1">Privacy Policy</BrandedHeading>
        <RawsterBrand className="rawster-brand-subtitle" />
        <p className="rawster-doc-updated">Last updated: June 1, 2026</p>
      </header>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">Introduction</BrandedHeading>
        <p>
          This Privacy Policy describes how Rawster (&quot;we,&quot; &quot;us,&quot; or
          &quot;our&quot;) handles information when you use the Rawster mobile
          application and related services (the &quot;App&quot;). Rawster is a dating and
          connection companion: private Radar notes are designed to stay on your device,
          while signed-in features such as Connections, Pulses, and optional Rawster AI
          sync or process data as described below.
        </p>
        <p>
          We do not sell your personal information. We do not use your private Radar
          journal for advertising profiling.
        </p>
      </section>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">Information We Collect</BrandedHeading>
        <p>Depending on how you use the App, we may collect or process:</p>
        <ul>
          <li>
            <strong>Account information</strong>, including Sign in with Apple or phone
            number sign-in (Firebase Authentication). If you use Sign in with Apple,
            Apple may provide your name and email once at first sign-in; we use that to
            create your profile and store your Apple-provided email with your account. We
            do not ask you to create a separate email-and-password account afterward.
          </li>
          <li>
            <strong>Phone number</strong>, if you sign in with phone; optional
            discoverability may use a hashed phone lookup map when you enable those
            features.
          </li>
          <li>
            <strong>Profile and connection data</strong> when signed in, such as display
            name, bio, gender, dating intent, interested-in, relationship status,
            attachment style, depth-map answers, connections, pulses, questions and
            answers, invites, timeline, and optional published Rawster Score.
          </li>
          <li>
            <strong>User content</strong>, including messages you send to Rawster AI and
            related in-app content for signed-in features.
          </li>
          <li>
            <strong>Sensitive information</strong>, such as dating preferences you add to
            your profile.
          </li>
          <li>
            <strong>Device identifiers</strong>, including push notification tokens (FCM)
            and optional primary-device binding for account security.
          </li>
          <li>
            <strong>Purchase information</strong>, subscription status via Apple App Store
            and RevenueCat; we do not receive your full payment card details.
          </li>
        </ul>
        <p>
          <strong>On your device (not uploaded as account data by default):</strong> Most
          Radar data, including per-person notes, journal, hookup log, quick pulses,
          screenshot lookback analyses, and many settings, are stored locally (MMKV).
          Export and delete options for local data are in Settings.
        </p>
        <p>
          <strong>We do not collect</strong> your contact list (contacts are used only
          on-device when you pick someone to add), uploaded photo or video files (profile
          photos stay as local files unless you provide a public URL), location, health
          data, or financial account numbers. We do not use an in-app advertising or
          cross-app tracking analytics SDK.
        </p>
      </section>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">How We Use Your Information</BrandedHeading>
        <p>We use collected information to:</p>
        <ul>
          <li>Provide, operate, and improve the App and its features;</li>
          <li>
            Sync connection, profile, pulse, and related data when you sign in;
          </li>
          <li>Deliver push notifications you allow (pulses, connections, etc.);</li>
          <li>Process subscriptions and respond to support requests;</li>
          <li>Operate Rawster AI only after your in-app consent to send data to OpenAI;</li>
          <li>Detect abuse, fraud, and technical issues;</li>
          <li>Comply with legal obligations.</li>
        </ul>
        <p>
          Email from Apple is used for account and service purposes. We do not use it for
          unrelated marketing without your consent where required by law.
        </p>
      </section>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">Data Storage and Security</BrandedHeading>
        <p>
          Data you enter in Radar is largely stored on your device. When you sign in,
          certain data is stored in Google Firebase / Firestore (match profile,
          connections, pulses, questions, AI chat history, push tokens, and related
          fields). We do not upload profile photo image files to Firebase Storage in the
          App; photos are typically local file references unless you provide a public URL.
        </p>
        <p>
          Screenshot lookback uses on-device OCR (ML Kit); image files are not uploaded for
          OCR. If you use Rawster AI with lookback text, only text you choose to send may
          go to OpenAI after consent, not the image file.
        </p>
        <p>
          We use reasonable administrative, technical, and organizational safeguards. App
          lock / Face ID, when enabled, protects access on your device only. No method of
          transmission or storage is completely secure.
        </p>
      </section>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">Rawster AI and OpenAI</BrandedHeading>
        <p>
          Rawster AI is an optional Rawster Pro feature. Before your first AI request, you
          must see an in-app consent screen that explains Rawster AI uses OpenAI. You tap
          &quot;I agree — send my data to OpenAI&quot; to continue, or &quot;Not
          now&quot; — if you decline, no data is sent to OpenAI.
        </p>
        <p>If you agree, we may send to OpenAI (via our Firebase Cloud Function):</p>
        <ul>
          <li>Your typed message to Rawster AI;</li>
          <li>
            A structured summary of active connections (nicknames/display names and
            metrics such as pulses or compatibility — not private Radar notes by default);
          </li>
          <li>
            Optional text extracted on your device from a chat screenshot you select (not
            the image file);
          </li>
          <li>Prior messages in that AI thread for context.</li>
        </ul>
        <p>
          Not sent to OpenAI by design: your Apple ID, email, phone number, or full
          private Radar notes — unless you type them into the AI chat. AI chat history is
          also stored in your Firebase account.
        </p>
      </section>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">Data Retention</BrandedHeading>
        <p>
          We retain your information for as long as your account is active or as needed to
          provide the App. You may delete content within the App and export or delete
          local data in Settings. When you delete your account or request deletion, we will
          remove or anonymize your personal data within a reasonable period, except where
          retention is required by law.
        </p>
      </section>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">Third Party Services</BrandedHeading>
        <p>The App uses third party services that process data under their own policies:</p>
        <ul>
          <li>
            <strong>Google (Firebase)</strong> — Authentication, Firestore, Cloud
            Functions, Cloud Messaging (push)
          </li>
          <li>
            <strong>OpenAI</strong> — Rawster AI only, after your in-app consent
          </li>
          <li>
            <strong>RevenueCat</strong> — Subscription and entitlement management (may
            receive your Firebase user ID and purchase status)
          </li>
          <li>
            <strong>Apple</strong> — Sign in with Apple and App Store payments
          </li>
        </ul>
        <p>
          Operational infrastructure logs may exist to run these services securely; we do
          not use your relationship journal for advertising or declare cross-app tracking
          for targeted advertising across other companies&apos; apps.
        </p>
      </section>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">Your Rights and Choices</BrandedHeading>
        <p>
          Depending on your location, you may have rights to access, correct, delete, or
          export your personal data, and to object to or restrict certain processing. Use
          in-app account deletion where available, or contact us at{" "}
          <a href={`mailto:${RAWSTER_SUPPORT_EMAIL}`}>{RAWSTER_SUPPORT_EMAIL}</a>. We will
          respond within a reasonable timeframe as required by applicable law, including
          certain U.S. state privacy rights where they apply.
        </p>
      </section>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">Children&apos;s Privacy</BrandedHeading>
        <p>
          Rawster is not intended for children under 13 (or the minimum age required in
          your region). We do not knowingly collect personal information from children. If
          you believe a child has provided us data, contact us and we will take steps to
          delete it.
        </p>
      </section>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">International Users</BrandedHeading>
        <p>
          If you access the App from outside the United States, your information may be
          transferred to and processed in countries where we or our service providers
          operate. Those locations may have different data protection laws than your
          country.
        </p>
      </section>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">Changes to This Policy</BrandedHeading>
        <p>
          We may update this Privacy Policy from time to time. The &quot;Last
          updated&quot; date at the top will reflect changes. Continued use of the App
          after updates constitutes acceptance of the revised policy where permitted by
          law.
        </p>
      </section>

      <section className="rawster-doc-section">
        <BrandedHeading as="h2">Contact Us</BrandedHeading>
        <p>
          For privacy questions, data access requests, or deletion requests, email{" "}
          <a href={`mailto:${RAWSTER_SUPPORT_EMAIL}`}>{RAWSTER_SUPPORT_EMAIL}</a>.
        </p>
      </section>
    </main>
  );
}
